import { Link } from "@/lib/router-compat";
import { Helmet } from "react-helmet-async";
import SiteHeader from "@/components/SiteHeader";
import VideoBanner, { SidebarVideoBanners } from "@/components/blackbox/VideoBanner";
import { HelpCircle, ChevronRight, ExternalLink, BookOpen, MessageSquare } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

interface FAQItem {
  question: string;
  answer: string;
  relatedLinks?: { title: string; url: string }[];
}

const faqData: FAQItem[] = [
  {
    question: "O que são pápulas perladas p-nilianas (PPP)?",
    answer: "Pápulas perladas p-nilianas (PPP) são pequenas bolinhas em formato de cúpula que aparecem em fileiras ao redor da coroa (borda) da glande do p-nis. São variações anatômicas completamente normais, NÃO uma DST. Estudos mostram que 14% a 48% dos homens as possuem. Geralmente surgem na puberdade e permanecem por toda a vida.",
    relatedLinks: [
      { title: "Guia Completo sobre PPP", url: "/guias/pequenas-bolinhas-no-penis" },
      { title: "Artigo PPP vs HPV", url: "/artigos/ppp-vs-hpv" }
    ]
  },
  {
    question: "As pápulas perladas p-nilianas são contagiosas?",
    answer: "Não, as PPP NÃO são contagiosas. Elas fazem parte normal da sua anatomia, semelhante a uma pinta ou marca de nascença. Não podem ser transmitidas por contato sexual nem de nenhuma outra forma. Você nasce com o potencial de desenvolvê-las.",
    relatedLinks: [
      { title: "Artigo: PPP Explicado", url: "/artigos/ppp-explicada" }
    ]
  },
  {
    question: "As pápulas perladas p-nilianas somem sozinhas?",
    answer: "Não, geralmente as PPP não desaparecem por conta própria. São uma característica anatômica permanente que surge na puberdade. Porém, são inofensivas e não requerem tratamento. Alguns homens optam pela remoção estética por eletrocauterização, com taxa de sucesso de 100%.",
    relatedLinks: [
      { title: "Por que as bolinhas não somem", url: "/artigos/carocos-nao-desaparecem" }
    ]
  },
  {
    question: "Qual a diferença entre PPP e verrugas genitais?",
    answer: "As PPP aparecem em fileiras uniformes e organizadas ao redor da coroa, são lisas, estão presentes desde a puberdade e não mudam. As verrugas genitais têm textura rugosa, semelhante à couve-flor, surgem de forma aleatória, geralmente após contato sexual, e podem crescer ou se multiplicar. PPP é inofensivo; verrugas exigem tratamento.",
    relatedLinks: [
      { title: "Comparativo: PPP vs Verrugas", url: "/artigos/ppp-vs-verrugas-genitais" }
    ]
  },
  {
    question: "O que são as glândulas de Tyson?",
    answer: "Glândulas de Tyson é outro nome para a mesma condição das pápulas perladas p-nilianas (PPP). São pequenas glândulas sebáceas que aparecem ao redor da coroa do p-nis. Os termos são usados de forma intercambiável na literatura médica.",
    relatedLinks: [
      { title: "Artigo: Glândulas de Tyson", url: "/artigos/glandulas-de-tyson-explicadas" }
    ]
  },
  {
    question: "O que são as manchas de Fordyce?",
    answer: "As manchas de Fordyce são pequenas bolinhas claras ou amarelo-esbranquiçadas que aparecem na haste do p-nis, no escroto ou nos lábios. São glândulas sebáceas (de óleo) visíveis e estão presentes em até 80% dos adultos. Assim como as PPP, são totalmente inofensivas e não precisam de tratamento.",
    relatedLinks: [
      { title: "Guia Completo das Bolinhas", url: "/guias/pequenas-bolinhas-no-penis" }
    ]
  },
  {
    question: "Devo contar para a(o) parceira(o) sobre as PPP?",
    answer: "Como as PPP são uma variação anatômica normal (não são DST nem infecção), você não tem obrigação de revelar. Porém, se ficar mais confortável, pode explicar que são bolinhas normais e não contagiosas que muitos homens têm. Conhecimento ajuda a reduzir a ansiedade dos dois lados.",
    relatedLinks: [
      { title: "Intimidade com PPP", url: "/artigos/ppp-dicas-intimidade" }
    ]
  },
  {
    question: "É possível remover as PPP?",
    answer: "Sim, as PPP podem ser removidas por motivos estéticos por meio de eletrocauterização, laser de CO₂ ou crioterapia. A eletrocauterização é considerada o método mais eficaz, com taxa de sucesso de 100%. Como as PPP são inofensivas, a remoção é opcional e puramente estética.",
    relatedLinks: [
      { title: "Opções de Remoção de PPP", url: "/artigos/opcoes-remocao-ppp" }
    ]
  },
  {
    question: "Quando devo procurar um médico sobre bolinhas no p-nis?",
    answer: "Procure um médico se: as bolinhas surgiram após contato sexual, você sente dor/coceira/ardência, há secreção, as bolinhas são rugosas ou parecem couve-flor, estão crescendo ou se multiplicando, ou você tem bolhas dolorosas que aparecem e somem. Se as bolinhas estão estáveis desde a puberdade e sem sintomas, provavelmente são normais.",
    relatedLinks: [
      { title: "Guia: Quando se Preocupar", url: "/guias/pequenas-bolinhas-no-penis#when-worry" }
    ]
  },
  {
    question: "Bolhas na cabeça do p-nis são a mesma coisa que bolinhas?",
    answer: "Não. Bolinha é uma elevação sólida e indolor (geralmente PPP ou glândula sebácea, totalmente benigna). Bolha contém líquido, arde ou dói, surge de repente em grupos e pode virar ferida — esse padrão exige avaliação médica, pois pode indicar herpes ou outra infecção.",
    relatedLinks: [
      { title: "Guia: Bolhas e Bolinha na Cabeça do P-nis", url: "/guias/bolhas-na-cabeca-do-penis" }
    ]
  },
  {
    question: "Por que ninguém ensinou isso na educação sexual?",
    answer: "Infelizmente, a maior parte da educação sexual foca em DSTs e prevenção da gravidez, mas raramente aborda variações anatômicas normais. Isso faz com que muitos homens entrem em pânico ao notar características inofensivas como PPP ou manchas de Fordyce. É por isso que recursos educativos como este existem — para compartilhar informação precisa e reduzir ansiedade desnecessária."
  }
];

const FAQ = () => {
  // FAQPage schema for rich results
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  // BreadcrumbList schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://smothmethod.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "FAQ",
        "item": "https://smothmethod.com/perguntas-frequentes"
      }
    ]
  };

  return (
    <div className="min-h-screen bg-[#f6f7f9]">
      {/* Sidebar banners for desktop */}
      <SidebarVideoBanners />
      <Helmet>
        <title>Pápulas Perladas P-nilianas: Suas Dúvidas Respondidas</title>
        <meta name="description" content="Essas bolinhas são contagiosas? São DST? Vão desaparecer? Respostas claras às dúvidas mais comuns sobre PPP e glândulas de Tyson." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://smothmethod.com/perguntas-frequentes" />
        <link rel="alternate" hrefLang="pt-br" href="https://smothmethod.com/perguntas-frequentes" />
        <link rel="alternate" hrefLang="x-default" href="https://smothmethod.com/perguntas-frequentes" />
        <meta property="og:locale" content="pt_BR" />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="PPP e Glândulas de Tyson: Suas Dúvidas Respondidas" />
        <meta property="og:description" content="Essas bolinhas são contagiosas? São DST? Vão desaparecer? Respostas claras às dúvidas mais comuns." />
        <meta property="og:url" content="https://smothmethod.com/perguntas-frequentes" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="FAQ: PPP e Glândulas de Tyson" />
        <meta name="twitter:description" content="Respostas claras às dúvidas mais comuns sobre pápulas perladas p-nilianas." />

        {/* Schema */}
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      <SiteHeader />
      
      <main className="max-w-3xl mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <nav className="mb-6 text-sm" aria-label="Breadcrumb">
          <ol className="flex items-center gap-2 text-gray-500" itemScope itemType="https://schema.org/BreadcrumbList">
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <Link to="/" className="hover:text-[#4267B2]" itemProp="item">
                <span itemProp="name">Início</span>
              </Link>
              <meta itemProp="position" content="1" />
            </li>
            <li><ChevronRight className="w-4 h-4" /></li>
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <Link to="/perguntas-frequentes" itemProp="item">
                <span className="text-gray-700" itemProp="name">FAQ</span>
              </Link>
              <meta itemProp="position" content="2" />
            </li>
          </ol>
        </nav>

        {/* Header */}
        <header className="bg-white rounded-lg border border-gray-200 p-6 md:p-8 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-[#4267B2]/10 flex items-center justify-center">
              <HelpCircle className="w-6 h-6 text-[#4267B2]" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                Perguntas Frequentes
              </h1>
              <p className="text-gray-500 text-sm mt-1">
                Dúvidas comuns sobre PPP, glândulas de Tyson e bolinhas no p-nis
              </p>
            </div>
          </div>
          <p className="text-gray-600">
            Encontre respostas para as dúvidas mais frequentes sobre pápulas perladas p-nilianas (PPP), glândulas de Tyson, manchas de Fordyce e outras bolinhas no p-nis. Informação precisa e com embasamento médico.
          </p>
        </header>

        {/* Quick Links */}
        <div className="grid md:grid-cols-2 gap-4 mb-8">
          <Link 
            to="/guias/pequenas-bolinhas-no-penis"
            className="flex items-center gap-3 bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow"
          >
            <BookOpen className="w-5 h-5 text-[#4267B2]" />
            <div>
              <p className="font-medium text-gray-900">Guia Completo</p>
              <p className="text-sm text-gray-500">Informação aprofundada sobre todos os tipos de bolinhas</p>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400 ml-auto" />
          </Link>
          <Link 
            to="/artigos"
            className="flex items-center gap-3 bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow"
          >
            <MessageSquare className="w-5 h-5 text-[#4267B2]" />
            <div>
              <p className="font-medium text-gray-900">Artigos de Saúde</p>
              <p className="text-sm text-gray-500">Conteúdo educativo sobre saúde masculina</p>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400 ml-auto" />
          </Link>
        </div>

        {/* FAQ Accordion */}
        <section className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <Accordion type="single" collapsible className="divide-y divide-gray-100">
            {faqData.map((item, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="border-0">
                <AccordionTrigger className="px-6 py-4 text-left hover:no-underline hover:bg-gray-50">
                  <h2 className="font-medium text-gray-900 text-base pr-4">
                    {item.question}
                  </h2>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4">
                  <p className="text-gray-600 mb-4">{item.answer}</p>
                  {item.relatedLinks && item.relatedLinks.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <p className="text-xs text-gray-500 mb-2">Recursos relacionados:</p>
                      <div className="flex flex-wrap gap-2">
                        {item.relatedLinks.map((link, linkIdx) => (
                          <Link 
                            key={linkIdx}
                            to={link.url}
                            className="inline-flex items-center gap-1 text-sm text-[#4267B2] hover:underline"
                          >
                            <ExternalLink className="w-3 h-3" />
                            {link.title}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        {/* CTA Section */}
        <section className="mt-8 bg-gradient-to-r from-[#4267B2] to-[#5b7bd5] rounded-lg p-6 text-white">
          <h2 className="text-xl font-bold mb-2">Ainda tem dúvidas?</h2>
          <p className="text-white/90 mb-4">
            Navegue pelos nossos artigos de saúde para saber mais sobre PPP, glândulas de Tyson e temas relacionados.
          </p>
          <Link 
            to="/artigos"
            className="inline-flex items-center gap-2 bg-white text-[#4267B2] px-4 py-2 rounded-lg font-medium hover:bg-gray-100 transition-colors"
          >
            <MessageSquare className="w-4 h-4" />
            Ver Artigos
          </Link>
        </section>

        {/* Footer */}
        <footer className="text-center py-6 mt-8 border-t border-gray-200">
          <p className="text-xs text-gray-500">
            Estas informações são apenas para fins educativos. Sempre consulte um profissional médico em caso de dúvidas de saúde.
          </p>
        </footer>

        {/* Footer Video Banner - last element */}
        <VideoBanner />
      </main>
    </div>
  );
};

export default FAQ;
